import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTextos } from '../../context/regional-context';
import { textosComun } from '../../../i18n/modulos/comun';

export interface ChipOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
  count?: number;
  tint?: 'default' | 'primary' | 'emerald' | 'amber' | 'red' | 'purple' | 'blue';
  /** Tooltip nativo: se muestra al dejar el cursor sobre el chip. */
  title?: string;
}

interface ChipFilterProps<T extends string> {
  options: ChipOption<T>[];
  value: T;
  onChange: (v: T) => void;
  size?: 'sm' | 'md';
  className?: string;
}

// Mapa de tint → color del dot. Visible siempre (activo o inactivo) para que
// el ancho del chip no cambie al alternar selección (re-spec 2-jun-2026).
const TINT_DOT: Record<NonNullable<ChipOption<string>['tint']>, string> = {
  default: 'var(--muted-2)',
  primary: 'var(--blue-600)',
  emerald: 'var(--ok)',
  amber:   'var(--warn)',
  red:     'var(--danger)',
  purple:  '#9333EA',
  blue:    'var(--blue-600)',
};

/**
 * useHScrollOverflow — detecta si un contenedor scrollable horizontalmente
 * tiene contenido fuera de vista. Devuelve `{ left, right }` indicando si
 * hay scroll posible en cada dirección. Reactivo a resize del contenedor
 * y al scroll del usuario.
 *
 * Lo usamos para encender los fades del scroll horizontal (chip-segment,
 * categorías de Mesa, weekbar de Reservas, etc.) — el handoff de jun-2026
 * pidió que SIEMPRE haya un indicador visible cuando se puede deslizar.
 */
function useHScrollOverflow<T extends HTMLElement>(ref: React.RefObject<T | null>) {
  const [state, setState] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 1) {
        setState({ left: false, right: false });
        return;
      }
      setState({
        left:  el.scrollLeft > 1,
        right: el.scrollLeft < max - 1,
      });
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [ref]);
  return state;
}

/**
 * ChipFilter — segmentado horizontal con scroll en mobile.
 *
 * Diseño re-spec 2-jun-2026 (Filtros ordenados.html · A, paleta sk-viewseg):
 *   - Container pill `var(--bg-app)` sin borde.
 *   - Activo: blanco (`var(--surface)`) + texto `var(--blue-700)` + sombra.
 *   - Si el chip tiene tint != 'default', se muestra el dot SIEMPRE
 *     (también cuando está activo) para mantener el ancho fijo.
 *   - Una sola fila; si los chips no caben, scroll horizontal.
 *   - Fades izquierdo/derecho indican que hay más contenido para deslizar.
 */
export function ChipFilter<T extends string>({
  options, value, onChange, size = 'md', className = '',
}: ChipFilterProps<T>) {
  const tx = useTextos(textosComun);
  const scrollRef = useRef<HTMLDivElement>(null);
  const overflow = useHScrollOverflow(scrollRef);

  // El atributo data-overflow controla los gradientes ::before/::after en
  // el CSS (.chip-segment[data-overflow*="…"]).
  const overflowAttr = `${overflow.left ? 'left ' : ''}${overflow.right ? 'right' : ''}`.trim();

  // Scroll helpers — click en el chevron desplaza el carril ~80% de su ancho.
  const scrollBy = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <div
      className={`chip-segment chip-segment--${size} ${className}`}
      data-overflow={overflowAttr || undefined}
    >
      {/* Chevrons explícitos — sólo se muestran si hay overflow en esa
          dirección. Pedido usuario jun-2026: indicar claramente que el
          carril es scrolleable. */}
      {overflow.left && (
        <button
          type="button"
          aria-label={tx('desplazarIzquierda')}
          onClick={() => scrollBy(-1)}
          className="chip-segment__nav chip-segment__nav--left"
        >
          <ChevronLeft className="w-[14px] h-[14px]" strokeWidth={2.6} />
        </button>
      )}
      {overflow.right && (
        <button
          type="button"
          aria-label={tx('desplazarDerecha')}
          onClick={() => scrollBy(1)}
          className="chip-segment__nav chip-segment__nav--right"
        >
          <ChevronRight className="w-[14px] h-[14px]" strokeWidth={2.6} />
        </button>
      )}
      <div ref={scrollRef} className="chip-segment__scroll" role="tablist">
        {options.map(opt => {
          const isActive = opt.value === value;
          const showDot = !!(opt.tint && opt.tint !== 'default');
          return (
            <button
              key={opt.value}
              role="tab"
              aria-selected={isActive}
              title={opt.title}
              onClick={() => onChange(opt.value)}
              className={`chip-segment__btn ${isActive ? 'is-active' : ''}`}
            >
              {showDot && (
                <span
                  className="chip-segment__dot"
                  style={{ backgroundColor: TINT_DOT[opt.tint!] }}
                  aria-hidden="true"
                />
              )}
              <span className="chip-segment__label">{opt.label}</span>
              {typeof opt.count === 'number' && (
                <span className={`chip-segment__count ${isActive ? 'is-active' : ''}`}>
                  {opt.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Re-export el hook para que otros componentes (Mesa workspace, reservations
// weekbar, etc.) puedan reusarlo y mostrar indicadores de scroll consistentes.
export { useHScrollOverflow };
